import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, from, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { StaffApiItem } from '../models/staff.model';
import { paginatedItems } from '../utils/paginated.util';
import { AccountStatusService } from './account-status.service';
import { AuthService } from './auth.service';
import { SessionLifecycleService } from './session-lifecycle.service';
import { StaffService } from './staff.service';

/**
 * The logged-in user's staff record, resolved once per session.
 *
 * Two things hang off it and would otherwise each look it up: the permission rules, which
 * read the staff type, and the create screen, which reads the contracts and locations the
 * record is associated with. Sharing it here keeps that to one request per session instead
 * of two identical ones.
 *
 * Usually not even that one: the account-status check that runs on every login and session
 * restore already reads all of the user's staff records, so the record is taken from there
 * and filter-paginated is only asked when the check has nothing for this workspace.
 *
 * Every failure resolves to null, which both callers treat as the most restricted answer —
 * no role, and no contracts.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionStaffService {
  private readonly authService = inject(AuthService);
  private readonly staffService = inject(StaffService);
  private readonly accountStatus = inject(AccountStatusService);
  private readonly lifecycle = inject(SessionLifecycleService);

  private resolution: Observable<StaffApiItem | null> | null = null;

  constructor() {
    // The record belongs to whoever was logged in; the next session looks its own up.
    this.lifecycle.ended$
      .pipe(takeUntilDestroyed())
      .subscribe(() => (this.resolution = null));
  }

  /** Resolves the staff record, reusing the in-flight or already-settled lookup. */
  public resolveStaff(): Observable<StaffApiItem | null> {
    if (!this.resolution) {
      this.resolution = this.lookupStaff().pipe(
        // A lookup still in the air when the session ends is dropped rather than delivered,
        // so the previous user's record cannot land after the reset.
        takeUntil(this.lifecycle.ended$),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.resolution;
  }

  /**
   * The record's id, under whichever key the endpoint used.
   *
   * The staff endpoints report the primary key as `staffId` on some responses and `id` on
   * others, so both are read rather than trusting either.
   */
  public static idOf(staff: StaffApiItem | null): string {
    return staff?.id?.trim() || staff?.staffId?.trim() || '';
  }

  private lookupStaff(): Observable<StaffApiItem | null> {
    const userId = this.authService.getUserId();
    if (!userId) {
      console.warn('[session staff] No logged-in user — no staff record to read.');
      return of(null);
    }

    const workSpaceId = this.authService.getWorkspaceId();
    return this.staffFromStatusCheck(userId, workSpaceId).pipe(
      switchMap(staff => (staff ? of(staff) : this.fetchStaff(userId, workSpaceId))),
    );
  }

  /**
   * The record out of the account-status check, or null when it has none to offer.
   *
   * Picked the way the endpoint would have answered: this user's row in the active
   * workspace, or their first one when there is no workspace yet. Deleted rows are passed
   * over. A miss is not taken as "no record" — the check reads across workspaces without a
   * workspace filter, so the scoped request still gets the final word.
   */
  private staffFromStatusCheck(
    userId: string,
    workSpaceId: string | undefined,
  ): Observable<StaffApiItem | null> {
    const records = this.accountStatus.staffRecordsFor(userId);
    if (!records) {
      return of(null);
    }
    return from(records).pipe(
      map(staffs => staffs?.find(staff =>
        staff.userId === userId &&
        !staff.isDeleted &&
        (!workSpaceId || staff.workSpaceId === workSpaceId)) ?? null),
    );
  }

  private fetchStaff(userId: string, workSpaceId: string | undefined): Observable<StaffApiItem | null> {
    return this.staffService
      .filterPaginated({
        pagination: { pageNumber: 1, pageSize: 1 },
        userId,
        // Scoped to the workspace, so a staff record in another one cannot answer for this
        // user — a gestor may be staff in several.
        ...(workSpaceId ? { workSpaceId } : {}),
      })
      .pipe(
        map(response => {
          const staff = paginatedItems(response)[0] ?? null;
          if (!staff) {
            console.warn(
              `[session staff] No staff record for user ${userId} in workspace` +
              ` ${workSpaceId ?? '(none)'}.`,
            );
          }
          return staff;
        }),
        catchError((error: unknown) => {
          console.error('Unable to read the staff record', error);
          return of(null);
        }),
      );
  }
}
